/* JAPANIME.IT — Phase 30: exact series/vendor rails and local recently viewed history. */
(() => {
  const STORAGE_KEY = 'jp-recently-viewed-products-v1';
  const MAX_HISTORY = 12;
  const MAX_CARDS = 5;

  const readHistory = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (error) {
      return [];
    }
  };

  const writeHistory = (handles) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_HISTORY)));
    } catch (error) {
      // Browsing history remains optional when local storage is unavailable.
    }
  };

  const parseCards = (html, selector) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    return [...template.content.querySelectorAll(selector)];
  };

  const addCards = (rail, cards, currentProductId) => {
    const track = rail.querySelector('[data-jp-product-track]');
    if (!track) return;

    const seen = new Set();
    cards.forEach((card) => {
      const productId = card.dataset.productId;
      if (!productId || productId === currentProductId || seen.has(productId) || seen.size >= MAX_CARDS) return;
      seen.add(productId);
      track.append(card);
    });

    if (seen.size > 0) {
      rail.hidden = false;
      window.requestAnimationFrame(() => rail.jpUpdateControls?.());
      // GroPulse listens for this event and rescans cards added after the
      // initial page load. The recommendation rails are populated by fetch,
      // so without the event their hearts remain inert fallbacks.
      document.dispatchEvent(new CustomEvent('products:updated'));
      window.setTimeout(() => document.dispatchEvent(new CustomEvent('products:updated')), 350);
    }
  };

  const loadRemoteRail = async (rail, currentProductId) => {
    const source = rail.dataset.source;
    if (!source) return;
    try {
      const response = await fetch(source, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      addCards(rail, parseCards(await response.text(), '[data-jp-discovery-card]'), currentProductId);
    } catch (error) {
      // A failed recommendation request never blocks the product page.
    }
  };

  const loadRecentRail = async (rail, currentProductId, currentHandle, rootUrl) => {
    const previousHandles = [...new Set(readHistory())].filter((handle) => handle !== currentHandle);
    const candidates = previousHandles.slice(0, MAX_HISTORY);
    const base = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    writeHistory([currentHandle, ...previousHandles]);

    const responses = await Promise.all(
      candidates.map(async (handle) => {
        try {
          const response = await fetch(`${base}products/${encodeURIComponent(handle)}?view=jp-recent-card`, {
            headers: { Accept: 'text/html' },
          });
          return response.ok ? response.text() : '';
        } catch (error) {
          return '';
        }
      }),
    );

    const cards = responses.flatMap((html) => (html ? parseCards(html, '[data-jp-recent-card]') : []));
    addCards(rail, cards, currentProductId);
  };

  const setupControls = (rail) => {
    const track = rail.querySelector('[data-jp-product-track]');
    const previous = rail.querySelector('[data-jp-carousel-prev]');
    const next = rail.querySelector('[data-jp-carousel-next]');
    if (!track || !previous || !next) return;

    const controls = rail.querySelector('.jp-product-discovery__controls');

    const update = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const canMove = maxScroll > 3;
      const canMovePrevious = canMove && track.scrollLeft > 3;
      const canMoveNext = canMove && track.scrollLeft < maxScroll - 3;

      previous.hidden = !canMovePrevious;
      next.hidden = !canMoveNext;
      previous.disabled = !canMovePrevious;
      next.disabled = !canMoveNext;
      if (controls) controls.hidden = !canMovePrevious && !canMoveNext;
    };

    const move = (direction) => {
      track.scrollBy({ left: direction * Math.max(track.clientWidth * 0.9, 260), behavior: 'smooth' });
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update, { passive: true });
    rail.jpUpdateControls = update;
    update();
  };

  const init = (root) => {
    if (root.dataset.jpDiscoveryReady === 'true') return;
    root.dataset.jpDiscoveryReady = 'true';

    const currentProductId = root.dataset.currentProductId || '';
    const currentHandle = root.dataset.currentProductHandle || '';
    const rootUrl = root.dataset.rootUrl || '/';
    const rails = [...root.querySelectorAll('[data-jp-product-rail]')];

    rails.forEach(setupControls);
    rails.filter((rail) => !rail.hasAttribute('data-jp-recent-rail')).forEach((rail) => loadRemoteRail(rail, currentProductId));

    const recentRail = root.querySelector('[data-jp-recent-rail]');
    if (recentRail && currentHandle) loadRecentRail(recentRail, currentProductId, currentHandle, rootUrl);
  };

  const initializeAll = () => document.querySelectorAll('[data-jp-product-discovery]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
